import type { PartnerAnalytics } from "components/common/MonthlyEngagementPage/PartnerLevelAnalytics/types"

import type {
  CallsMetric,
  EngagementKpiMetric,
  ExecutiveMetric,
  GrowthMetric,
  MobileWeeklyTrend,
  ThermostatWeeklyTrend,
  TopDevice,
} from "./types"

export const DEFAULT_REPORTING_PERIOD = ""

export const executiveMetrics: Array<Pick<ExecutiveMetric, "title">> = [
  { title: "Total Engagement Actions" },
  { title: "Thermostat Engagement" },
  { title: "Mobile App Engagement" },
  { title: "Total Conversions" },
]

export const growthMetrics: Array<Pick<GrowthMetric, "title">> = [
  { title: "Customers" },
  { title: "Thermostats" },
  { title: "App Installs" },
]

export const thermostatKpis: Array<Pick<EngagementKpiMetric, "id" | "title" | "subtitle">> = [
  {
    id: "clicks",
    title: "Clicks on Logo",
    subtitle: "Customer interactions on thermostat devices",
  },
  {
    id: "qr-scans",
    title: "QR Scans",
    subtitle: "Scans of contractor QR code from thermostat interface.",
  },
]

export const thermostatWeeklyTrend: ThermostatWeeklyTrend[] = []

export const topDevices: TopDevice[] = []

export const mobileKpiGroups: Array<{
  title: string
  metrics: Array<Pick<EngagementKpiMetric, "id" | "title" | "subtitle">>
}> = [
  {
    title: "Navigation Actions",
    metrics: [
      {
        id: "mobile-clicks",
        title: "Clicks on Logo (App)",
        subtitle: "Clicks on contractor logo within the mobile app.",
      },
      {
        id: "support-clicks",
        title: "Support Tab Clicks",
        subtitle: "Clicks on the Support tab in the mobile app.",
      },
    ],
  },
  {
    title: "Website Actions",
    metrics: [
      {
        id: "website-actions",
        title: "Initial Website Clicks",
        subtitle: 'Initial clicks on "Visit Website" button in the mobile app.',
      },
      {
        id: "website-conversion",
        title: "Continue to Website (Conversion)",
        subtitle: "Confirmation clicks to proceed to contractor website.",
      },
    ],
  },
  {
    title: "Technician Requests",
    metrics: [
      {
        id: "technician-clicks",
        title: "Request Technician Clicks",
        subtitle: 'Initial clicks on "Request Technician" button in the mobile app.',
      },
      {
        id: "technician-conversion",
        title: "Continue to Request (Conversion)",
        subtitle: "Confirmation clicks to proceed with technician request.",
      },
    ],
  },
  {
    title: "Direct Contact Actions (High Intent)",
    metrics: [
      {
        id: "app-calls",
        title: "App Calls",
        subtitle: "Clicks to initiate phone call from the mobile app.",
      },
      {
        id: "app-texts",
        title: "App Texts",
        subtitle: "Clicks to initiate text message from the mobile app.",
      },
    ],
  },
]

export const mobileWeeklyTrend: MobileWeeklyTrend[] = []

export const callsSection: { metrics: Array<Pick<CallsMetric, "id" | "title" | "sourceLabel">> } = {
  metrics: [
    { id: "calls-taken", title: "Calls Taken", sourceLabel: "via Twilio" },
    { id: "connected", title: "Connected", sourceLabel: "via Twilio" },
    { id: "answered-by-human", title: "Answered by a Human", sourceLabel: "via Twilio" },
    { id: "avg-talk-time", title: "Avg Talk Time", sourceLabel: "via Twilio" },
  ],
}

export const partnerAnalytics: PartnerAnalytics[] = [
  {
    name: "Heating & Air (in-house)",
    source: "northpeak.com",
    badge: "Responsiveness added",
    badgeTone: "neutral",
    summaryStats: [
      { label: "Calls", value: 10 },
      { label: "Answered", value: "50%", tone: "neutral" },
    ],
    isExpandedByDefault: true,
    sections: [
      {
        title: "Thermostat Device",
        items: [
          { label: "Logo clicks", value: 14 },
          { label: "QR scans", value: 8 },
        ],
      },
      {
        title: "Mobile App",
        items: [
          { label: "Logo clicks", value: 40 },
          { label: "Support tab", value: 22 },
          { label: "Website visits", value: 26 },
          { label: "Request tech", value: 18 },
          { label: "Call / text", value: 10 },
        ],
      },
      {
        title: "Responsiveness",
        width: "half",
        items: [
          { label: "Calls received", value: 10 },
          { label: "Answered", value: 5 },
          { label: "Answer rate", value: "50%", progress: 50 },
          { label: "Avg ring before answer", value: "21s" },
        ],
      },
    ],
    thermostatLogoClicks: 14,
    thermostatQrScans: 8,
    mobileLogoClicks: 40,
    supportTabClicks: 22,
    websiteVisits: 26,
    requestTechClicks: 18,
    callTextActions: 10,
  },
  {
    name: "Electrical",
    source: "northpeak.com/electrical",
    summaryStats: [
      { label: "Calls", value: 14 },
      { label: "Answered", value: "57%", tone: "success" },
    ],
    sections: [
      {
        title: "Thermostat Device",
        items: [
          { label: "Logo clicks", value: 16 },
          { label: "QR scans", value: 10 },
        ],
      },
      {
        title: "Mobile App",
        items: [
          { label: "Logo clicks", value: 28 },
          { label: "Support tab", value: 16 },
          { label: "Website visits", value: 18 },
          { label: "Request tech", value: 14 },
          { label: "Call / text", value: 12 },
        ],
      },
      {
        title: "Responsiveness",
        width: "half",
        items: [
          { label: "Calls received", value: 14 },
          { label: "Answered", value: 8 },
          { label: "Answer rate", value: "57%", progress: 57 },
          { label: "Avg ring before answer", value: "18s" },
        ],
      },
    ],
    thermostatLogoClicks: 16,
    thermostatQrScans: 10,
    mobileLogoClicks: 28,
    supportTabClicks: 16,
    websiteVisits: 18,
    requestTechClicks: 14,
    callTextActions: 12,
  },
  {
    name: "Plumbing",
    source: "northpeak.com/plumbing",
    badge: "TOP",
    badgeTone: "success",
    summaryStats: [
      { label: "Calls", value: 8 },
      { label: "Answered", value: "88%", tone: "success" },
    ],
    sections: [
      {
        title: "Thermostat Device",
        items: [
          { label: "Logo clicks", value: 11 },
          { label: "QR scans", value: 6 },
        ],
      },
      {
        title: "Mobile App",
        items: [
          { label: "Logo clicks", value: 18 },
          { label: "Support tab", value: 12 },
          { label: "Website visits", value: 9 },
          { label: "Request tech", value: 8 },
          { label: "Call / text", value: 4 },
        ],
      },
      {
        title: "Responsiveness",
        width: "half",
        items: [
          { label: "Calls received", value: 8 },
          { label: "Answered", value: 7 },
          { label: "Answer rate", value: "88%", progress: 88 },
          { label: "Avg ring before answer", value: "14s" },
        ],
      },
    ],
    thermostatLogoClicks: 11,
    thermostatQrScans: 6,
    mobileLogoClicks: 18,
    supportTabClicks: 12,
    websiteVisits: 9,
    requestTechClicks: 8,
    callTextActions: 4,
  },
  {
    name: "Solar",
    source: "northpeak.com/solar",
    summaryStats: [
      { label: "Calls", value: 9 },
      { label: "Answered", value: "78%", tone: "success" },
    ],
    sections: [
      {
        title: "Thermostat Device",
        items: [
          { label: "Logo clicks", value: 9 },
          { label: "QR scans", value: 5 },
        ],
      },
      {
        title: "Mobile App",
        items: [
          { label: "Logo clicks", value: 20 },
          { label: "Support tab", value: 14 },
          { label: "Website visits", value: 15 },
          { label: "Request tech", value: 10 },
          { label: "Call / text", value: 6 },
        ],
      },
      {
        title: "Responsiveness",
        width: "half",
        items: [
          { label: "Calls received", value: 9 },
          { label: "Answered", value: 7 },
          { label: "Answer rate", value: "78%", progress: 78 },
          { label: "Avg ring before answer", value: "17s" },
        ],
      },
    ],
    thermostatLogoClicks: 9,
    thermostatQrScans: 5,
    mobileLogoClicks: 20,
    supportTabClicks: 14,
    websiteVisits: 15,
    requestTechClicks: 10,
    callTextActions: 6,
  },
  {
    name: "Gas",
    source: "northpeak.com/gas",
    summaryStats: [
      { label: "Calls", value: 6 },
      { label: "Answered", value: "83%", tone: "success" },
    ],
    sections: [
      {
        title: "Thermostat Device",
        items: [
          { label: "Logo clicks", value: 8 },
          { label: "QR scans", value: 4 },
        ],
      },
      {
        title: "Mobile App",
        items: [
          { label: "Logo clicks", value: 16 },
          { label: "Support tab", value: 11 },
          { label: "Website visits", value: 8 },
          { label: "Request tech", value: 6 },
          { label: "Call / text", value: 3 },
        ],
      },
      {
        title: "Responsiveness",
        width: "half",
        items: [
          { label: "Calls received", value: 6 },
          { label: "Answered", value: 5 },
          { label: "Answer rate", value: "83%", progress: 83 },
          { label: "Avg ring before answer", value: "12s" },
        ],
      },
    ],
    thermostatLogoClicks: 8,
    thermostatQrScans: 4,
    mobileLogoClicks: 16,
    supportTabClicks: 11,
    websiteVisits: 8,
    requestTechClicks: 6,
    callTextActions: 3,
  },
  {
    name: "Pool",
    source: "northpeak.com/pool",
    badge: "NEEDS ATTENTION",
    badgeTone: "warning",
    summaryStats: [
      { label: "Calls", value: 5 },
      { label: "Answered", value: "40%", tone: "warning" },
    ],
    sections: [
      {
        title: "Thermostat Device",
        items: [
          { label: "Logo clicks", value: 6 },
          { label: "QR scans", value: 3 },
        ],
      },
      {
        title: "Mobile App",
        items: [
          { label: "Logo clicks", value: 9 },
          { label: "Support tab", value: 5 },
          { label: "Website visits", value: 7 },
          { label: "Request tech", value: 4 },
          { label: "Call / text", value: 2 },
        ],
      },
      {
        title: "Responsiveness",
        width: "half",
        items: [
          { label: "Calls received", value: 5 },
          { label: "Answered", value: 2 },
          { label: "Answer rate", value: "40%", progress: 40 },
          { label: "Avg ring before answer", value: "29s" },
        ],
      },
    ],
    thermostatLogoClicks: 6,
    thermostatQrScans: 3,
    mobileLogoClicks: 9,
    supportTabClicks: 5,
    websiteVisits: 7,
    requestTechClicks: 4,
    callTextActions: 2,
  },
]
